import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, Target, Heart, Globe, ArrowRight, Linkedin, Twitter } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-20">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-6">About Wayo</h1>
          <p className="text-lg sm:text-xl max-w-3xl mx-auto opacity-90">
            Empowering students worldwide to navigate their path to higher education with confidence and clarity.
          </p>
        </div>
      </div>

      {/* Quick Links */}
      <div className="max-w-4xl mx-auto px-4 -mt-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link to="/about/mission">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                  <Target className="w-6 h-6 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">Our Mission</h3>
                  <p className="text-gray-600 text-sm">Why we're building Wayo</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </CardContent>
            </Card>
          </Link>
          <Link to="/about/founders">
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">Our Founder</h3>
                  <p className="text-gray-600 text-sm">Meet the person behind Wayo</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400" />
              </CardContent>
            </Card>
          </Link>
        </div>
      </div>

      {/* Story Section */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-6">Our Story</h2>
            <p className="text-gray-600 mb-4">
              Wayo was born from firsthand experience. Our founder, Hamza, came to the US as an international student in 2019, hassling through the complex maze of college applications, visa processes, and cultural adjustments all on his own. Along the way, he encountered countless fraudulent "consultants" offering false promises and misleading advice.
            </p>
            <p className="text-gray-600 mb-4">
              Through this journey, he saw how the college application process, especially for international students, was fragmented, confusing, and unfair. Students with fewer resources struggled the most, while even those with access to guidance were often exploited by advisors motivated by commissions rather than care.
            </p>
            <p className="text-gray-600 mb-4">
              That's why we built Wayo: to level the playing field and empower every student, regardless of their background, with the tools, transparency, and guidance they need to chart their own path forward.
            </p>
            <div className="mt-6 p-4 bg-emerald-50 border-l-4 border-emerald-500 rounded-r-lg">
              <p className="text-gray-700 font-medium">No more dependency on commission driven counselors.</p>
              <p className="text-gray-700 font-medium">No more misleading promises.</p>
              <p className="text-gray-700 font-medium">No more trading futures for money.</p>
              <p className="text-emerald-700 font-semibold mt-2">At Wayo, we're here to make sure every student can find their way forward, honestly.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Card className="p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600">Growing</div>
              <div className="text-gray-600 text-sm">Student Community</div>
            </Card>
            <Card className="p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600">Expanding</div>
              <div className="text-gray-600 text-sm">College Network</div>
            </Card>
            <Card className="p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600">Global</div>
              <div className="text-gray-600 text-sm">Reach</div>
            </Card>
            <Card className="p-6 text-center">
              <div className="text-3xl font-bold text-emerald-600">24/7</div>
              <div className="text-gray-600 text-sm">AI Support</div>
            </Card>
          </div>
        </div>
      </div>

      {/* Values Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">Our Values</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Accessibility</h3>
              <p className="text-gray-600">Making quality education guidance available to all students, everywhere.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Transparency</h3>
              <p className="text-gray-600">Clear, honest information about the admission process without hidden agendas.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Community</h3>
              <p className="text-gray-600">Building connections between students, counselors, and institutions.</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="text-3xl font-bold mb-4">Ready to Find Your Way?</h2>
        <p className="text-gray-600 mb-8">Join thousands of students who have found their path to higher education with Wayo.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/signup">
            <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-8">
              Get Started Free
            </Button>
          </Link>
          <Link to="/contact">
            <Button size="lg" variant="outline" className="px-8">
              Contact Us
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default About;
